import * as React from "react"
import { Link, useRouteMatch } from "react-router-dom"
import { svgIconStyles } from "@digits-shared/components/SVG/svgIconStyles"
import { SvgGrid01Solid } from "@digits-shared/components/SVGIcons/solid/Grid01Solid.svg"
import { SvgPlusSquareSolid } from "@digits-shared/components/SVGIcons/solid/PlusSquareSolid.svg"
import numberHelper from "@digits-shared/helpers/numberHelper"
import stringHelper from "@digits-shared/helpers/stringHelper"
import { useCurrentRoute } from "@digits-shared/hooks/useCurrentRoute"
import useSession from "@digits-shared/hooks/useSession"
import colors from "@digits-shared/themes/colors"
import styled from "styled-components"
import { useOnAddClient } from "src/frontend/components/OS/Header/AddClient/useOnAddClient"
import { BadgeName, useBadgeContext } from "src/frontend/components/Shared/Contexts/BadgeContext"
import { BusinessName, Pending } from "src/frontend/components/Shared/NavSidebar/Menu/menuStyles"
import { calculatePathRoute } from "src/frontend/components/Shared/NavSidebar/Menu/sidebarMenuFunctions"
import { SIDEBAR_MENU_SIDE_PADDING } from "src/frontend/components/Shared/NavSidebar/sidebarConstants"
import { Badge } from "src/frontend/components/Shared/NavSidebar/sidebarShared"
import { useFrontendPathGenerator } from "src/frontend/hooks/useFrontendPathGenerator"
import routes from "src/frontend/routes"
import type FrontendSession from "src/frontend/session"
import type SessionBusinessOrganization from "src/frontend/session/BusinessOrganization"
import type SessionLegalEntity from "src/frontend/session/LegalEntity"
import type { SessionOrganization } from "src/frontend/session/Organization"
import { EntityIcon } from "src/shared/components/ObjectEntities/EntityIcon"

/*
  STYLES
*/

const BusinessIcon = styled(EntityIcon)`
  margin: 0;
  padding: 0;
`

export const EntityLink = styled(Link)<{ disabled?: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  cursor: pointer;
  color: ${colors.secondary};

  border-radius: 4px;
  padding: 8px;

  &.selected {
    border: 1px solid ${colors.translucentWhite70};
    background: ${colors.translucentAccentBlue10};
  }

  &[disabled] {
    pointer-events: none;
  }

  &:hover {
    background: ${colors.translucentAccentBlue05};
  }

  &:focus-visible {
    // ensure the outline is visible within the menu
    outline-offset: -2px;
  }
`

const BusinessContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  overflow: hidden;
  &.pending {
    // the "pending" label causes the row to grow taller than the others,
    // so we offset to keep the spacing the same while avoiding clipping
    margin-top: -3px;
  }
`

const BusinessInfo = styled.div`
  overflow: hidden;
`

const AddIcon = styled(SvgPlusSquareSolid)`
  width: 24px;
  height: 24px;
  padding: 1px;
  ${svgIconStyles(colors.accentTurquoise)};
`

const ViewAllIcon = styled(SvgGrid01Solid)`
  width: 24px;
  height: 24px;
  padding: 2px;
  ${svgIconStyles(colors.secondary)};
`

const Row = styled.div`
  width: 100%;
  padding: 0 ${SIDEBAR_MENU_SIDE_PADDING}px 6px;
`

/*
  INTERFACES
*/

export interface MenuItemsProps {
  onItemClick: () => void
}

/*
  COMPONENTS
*/

export const BusinessRowItem: React.FC<{
  iconUrl: string | undefined
  name: string
  isPending?: boolean
  badgeCount?: number | false
}> = ({ iconUrl, name, isPending, badgeCount }) => (
  <>
    <BusinessContainer className={stringHelper.classNames(isPending && "pending")}>
      <BusinessIcon fontSize={12} size={24} imageUrl={iconUrl}>
        {name}
      </BusinessIcon>
      <BusinessInfo>
        <BusinessName className={stringHelper.classNames(isPending && "pending")}>
          {name}
        </BusinessName>
        {isPending && <Pending>Pending</Pending>}
      </BusinessInfo>
    </BusinessContainer>
    {badgeCount && <Badge>{numberHelper.formatWithLimit(badgeCount, 99)}</Badge>}
  </>
)
export const AddBusinessRow: React.FC<{ onItemClick?: () => void }> = ({ onItemClick }) => {
  const { isDigitsEmployee } = useSession()
  const pathGenerator = useFrontendPathGenerator()

  // Currently blocked by 'onboard-add-new-business-disabled'
  if (!isDigitsEmployee) return null

  return (
    // TODO: Cannot route to /welcome for AIAccounting plans
    <EntityLink to={pathGenerator(routes.welcome)} onClick={onItemClick}>
      <BusinessContainer>
        <AddIcon />
        <BusinessInfo>
          <BusinessName>Add a Business</BusinessName>
        </BusinessInfo>
      </BusinessContainer>
    </EntityLink>
  )
}

export const AddClientRow: React.FC<{ onItemClick?: () => void }> = ({ onItemClick }) => {
  const { addClientPath, loading, canAddClient } = useOnAddClient()

  if (loading || !canAddClient) {
    return null
  }

  return (
    <Row>
      <EntityLink to={addClientPath} onClick={onItemClick}>
        <BusinessContainer>
          <AddIcon />
          <BusinessInfo>
            <BusinessName>Add a Client</BusinessName>
          </BusinessInfo>
        </BusinessContainer>
      </EntityLink>
    </Row>
  )
}

export const ViewAllRow: React.FC<{
  org: SessionBusinessOrganization
  onItemClick?: () => void
}> = ({ org, onItemClick }) => {
  const pathGenerator = useFrontendPathGenerator()

  return (
    <EntityLink
      to={pathGenerator(routes.firmDashboardFirmKnown, { orgSlug: org.slug })}
      onClick={onItemClick}
    >
      <BusinessContainer>
        <ViewAllIcon />
        <BusinessInfo>
          <BusinessName>View All Clients</BusinessName>
        </BusinessInfo>
      </BusinessContainer>
    </EntityLink>
  )
}

export const EntityListItem: React.FC<{
  isSelected: boolean
  organization: SessionOrganization
  legalEntity: SessionLegalEntity
  forceHome?: boolean
  onItemClick?: () => void
}> = ({ isSelected, legalEntity, organization, forceHome, onItemClick }) => {
  const { badgeCount } = useBadgeContext()
  const curRoute = useCurrentRoute(routes)
  const pathGenerator = useFrontendPathGenerator()
  const { doppelganger } = useSession<FrontendSession>()
  const actionItemsCount = badgeCount(legalEntity.id, BadgeName.ActionItems) || 0
  const isFirmDashboard = useRouteMatch(routes.firmDashboardRoot.parameterizedPath)

  const { iconUrl } = organization
  const pathRoute = calculatePathRoute(curRoute, forceHome || Boolean(isFirmDashboard))

  const isLEPending =
    !(legalEntity.hasDashboardAccess() || legalEntity.accountingViewVersions.size) ||
    legalEntity.isPending ||
    // NOTE: it is important to include the `isNew` check here for operator onboarding
    // so that the link is not clickable since they should not leave the onboarding flow
    legalEntity.isNew

  const showBadge = !isLEPending && actionItemsCount > 0

  return (
    <EntityLink
      to={pathGenerator(pathRoute, { leSlug: legalEntity.slug })}
      onClick={onItemClick}
      className={stringHelper.classNames(isSelected && "selected")}
      disabled={isLEPending && !doppelganger?.hasFullAccess}
      replace
    >
      <BusinessRowItem
        name={legalEntity.name}
        iconUrl={iconUrl}
        isPending={isLEPending}
        badgeCount={showBadge && actionItemsCount}
      />
    </EntityLink>
  )
}
